import { apiClient } from './api';
import { csrfService } from './csrf.service';

const TOKEN_KEY = 'token';
const ACCESS_TOKEN_KEY = 'access_token';
const USER_KEY = 'user';
const REFRESH_TOKEN_KEY = 'refresh_token';

export type AuthUser = {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  supplierId?: number | null;
  supplierName?: string | null;
};

export type LoginRequest = {
  email: string;
  password: string;
};

export type LoginResponse = {
  access_token: string;
  user: AuthUser;
};

export type ForgotPasswordRequest = {
  email: string;
};

export type ResetPasswordRequest = {
  token: string;
  password: string;
};

export type AcceptInviteRequest = {
  token: string;
  password: string;
};

type StoredUser = AuthUser & {
  name: string;
};

const toStoredUser = (user: AuthUser): StoredUser => ({
  ...user,
  name: `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim(),
});

export const authService = {
  login: async (payload: LoginRequest) => {
    const session = await apiClient.post<LoginResponse, LoginRequest>('/auth/login', payload);
    const storedUser = toStoredUser(session.user);

    localStorage.setItem(TOKEN_KEY, session.access_token);
    localStorage.setItem(ACCESS_TOKEN_KEY, session.access_token);
    localStorage.setItem(USER_KEY, JSON.stringify(storedUser));

    // Fetch CSRF token for subsequent requests
    try {
      await csrfService.fetchToken();
    } catch (err) {
      console.error('Failed to fetch CSRF token after login:', err);
      // Don't block login if CSRF fetch fails
    }

    return session;
  },

  forgotPassword: (payload: ForgotPasswordRequest) =>
    apiClient.post<{ success?: boolean; message?: string }, ForgotPasswordRequest>(
      '/auth/forgot-password',
      payload,
    ),

  resetPassword: (payload: ResetPasswordRequest) =>
    apiClient.post<{ success?: boolean; message?: string }, ResetPasswordRequest>(
      '/auth/reset-password',
      payload,
    ),

  acceptInvite: (payload: AcceptInviteRequest) =>
    apiClient.post<{ success?: boolean; message?: string }, AcceptInviteRequest>(
      '/auth/accept-invite',
      payload,
    ),

  changePassword: (payload: { currentPassword: string; newPassword: string }) =>
    apiClient.put<{ success: boolean; message?: string }, { currentPassword: string; newPassword: string }>(
      '/auth/change-password',
      payload,
    ),

  getStoredUser: (): StoredUser | null => {
    const userJson = localStorage.getItem(USER_KEY);
    if (!userJson) return null;

    try {
      return JSON.parse(userJson) as StoredUser;
    } catch {
      return null;
    }
  },

  getToken: () => localStorage.getItem(TOKEN_KEY) || localStorage.getItem(ACCESS_TOKEN_KEY),

  logout: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    csrfService.clearToken();
  },
};
